import { FC } from "react";
import { FormattedMessage } from "react-intl";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";

import { Button } from "~/components/ui/button";

interface DeletePageDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onCancel: () => void;
  onDelete: () => void;
  selectedCount?: number;
  type?: "page" | "section";
  name?: string;
  sectionNames?: string[];
}

export const DeletePageDialog: FC<DeletePageDialogProps> = ({
  open,
  setOpen,
  onCancel,
  onDelete,
  selectedCount = 1,
  type = "page",
  name,
  sectionNames = [],
}) => {
  const isMultiplePages = selectedCount > 1;
  const isMultipleSections = type === "section" && selectedCount > 1;

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onDelete();
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {type === "page" ? (
              <FormattedMessage
                id={
                  isMultiplePages
                    ? "docList.deletePages.title"
                    : "docList.deletePage.title"
                }
                defaultMessage={
                  isMultiplePages ? "Delete Pages" : "Delete Page"
                }
              />
            ) : (
              <FormattedMessage
                id={
                  isMultipleSections
                    ? "docList.deleteSections.title"
                    : "docList.deleteSection.title"
                }
                defaultMessage={
                  isMultipleSections ? "Delete Sections" : "Delete Section"
                }
              />
            )}
          </AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogDescription className="flex flex-col gap-[20px]">
          {type === "page" ? (
            <span>
              {isMultiplePages ? (
                <FormattedMessage
                  id="docList.deletePages.areYouSure"
                  values={{ count: selectedCount }}
                />
              ) : (
                <FormattedMessage
                  id="docList.deletePage.areYouSure"
                  defaultMessage="Are you sure you want to remove the page from this version?"
                />
              )}
            </span>
          ) : (
            <div className="flex flex-col gap-2">
              {isMultipleSections ? (
                <>
                  <span>
                    <FormattedMessage
                      id="docList.deleteSections.areYouSure"
                      defaultMessage="Are you sure you want to remove the following sections from this version?"
                    />
                  </span>
                  <ul className="list-disc list-inside max-h-[200px] overflow-y-auto prose prose-sm dark:prose-invert">
                    {sectionNames.map((sectionName, index) => (
                      <li key={index} className="text-sm">
                        <strong>{sectionName}</strong>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <span>
                  <FormattedMessage
                    id="docList.deleteSection.areYouSure"
                    defaultMessage="Are you sure you want to remove the section {name} from this version?"
                    values={{ name: <strong>{name}</strong> }}
                  />
                </span>
              )}
            </div>
          )}
          <span>
            <FormattedMessage
              id="docList.deletePage.aboutChanges"
              defaultMessage="The changes made will not affect the original files."
            />
          </span>
        </AlertDialogDescription>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          <Button onClick={handleDelete} variant="destructive">
            <FormattedMessage id="delete" defaultMessage="Delete" />
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};
